(() => {
  const ELEMENT_NAME = 'ag-main-product';
  const STATE = '[data-ag-product-state]';
  const SUMMARY = '[data-ag-summary]';
  const CONTENT = '[data-ag-content]';
  const OPTION = '[data-ag-option]';
  const THUMBNAIL = '[data-ag-thumbnail]';
  const IMAGE = '[data-ag-image]';
  const GALLERY = '[data-ag-gallery-slider]';
  const THUMBNAILS = '[data-ag-gallery-thumbnails]';
  const GALLERY_CONTROL = '[data-ag-gallery-step]';
  const GALLERY_SPEED = 480;
  const THUMBNAIL_WHEEL_EASING = 55;
  const THUMBNAIL_WHEEL_SENSITIVITY = 0.9;
  const DRAG_THRESHOLD = 8;
  const SUBMIT = '[name="add"]';
  const VARIANT_INPUT = '[name="id"]';
  const QUANTITY = '[name="quantity"]';
  const MINUS = '.ag-main-product__quantity [name="minus"]';
  const PLUS = '.ag-main-product__quantity [name="plus"]';
  const ERROR = '[data-ag-error]';
  const ERROR_DISPLAY_DURATION = 3000;
  const ACCORDION = '[data-ag-accordion]';
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  class AgMainProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.preProcessHtmlCallbacks = [];
      this.state = JSON.parse(this.querySelector(STATE).textContent);
      this.syncImage(this.activeImageId || this.state.imageId);
      this.onThumbnailFocusFrame = this.updateThumbnailFocusFrame.bind(this);
      this.initializeGallery();
      this.syncQuantityControls();
      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onPopState = this.handlePopState.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('scroll', this.onThumbnailFocusFrame, true);
      this.addEventListener('submit', this.onSubmit, true);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('resize', this.onThumbnailFocusFrame);
      this.unsubscribeCart = typeof subscribe === 'function'
        ? subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshQuantityRules()) : null;
    }

    disconnectedCallback() {
      this.clearErrorMessage();
      this.controller?.abort();
      this.quantityController?.abort();
      this.unsubscribeCart?.();
      this.motionPreference?.removeEventListener('change', this.onMotionChange);
      this.cancelThumbnailWheel();
      this.thumbnailGallery?.el.removeEventListener('wheel', this.onThumbnailWheel);
      this.gallery?.destroy(true, true);
      this.gallery = null;
      this.thumbnailGallery?.destroy(true, true);
      this.thumbnailGallery = null;
      this.syncImage(this.activeImageId);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('scroll', this.onThumbnailFocusFrame, true);
      this.removeEventListener('submit', this.onSubmit, true);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('resize', this.onThumbnailFocusFrame);
      this.initialized = false;
    }

    addPreProcessCallback(callback) {
      this.preProcessHtmlCallbacks.push(callback);
    }

    clearErrorMessage() {
      window.clearTimeout(this.errorMessageTimer);
      this.errorMessageTimer = undefined;
      const errorElement = this.querySelector(ERROR);
      if (!errorElement) return;
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    showErrorMessage(message) {
      this.clearErrorMessage();
      const errorElement = this.querySelector(ERROR);
      if (!errorElement) return;
      errorElement.textContent = message;
      errorElement.hidden = false;
      this.errorMessageTimer = window.setTimeout(() => this.clearErrorMessage(), ERROR_DISPLAY_DURATION);
    }

    handleClick(event) {
      const thumbnail = event.target.closest(THUMBNAIL);
      if (thumbnail && this.contains(thumbnail)) {
        if (this.thumbnailGallery?.allowClick === false) return;
        this.selectImage(thumbnail.dataset.agThumbnail, true);
      }
      const control = event.target.closest(GALLERY_CONTROL);
      if (control && this.contains(control)) {
        const images = Array.from(this.querySelectorAll(IMAGE));
        if (images.length < 2) return;
        const currentIndex = images.findIndex(image => image.dataset.agImage === this.activeImageId);
        const nextIndex = (currentIndex + Number(control.dataset.agGalleryStep) + images.length) % images.length;
        this.selectImage(images[nextIndex].dataset.agImage, true);
      }
    }

    handleFocusIn(event) {
      const thumbnail = event.target.closest(THUMBNAIL);
      if (thumbnail?.matches(':focus-visible')) {
        this.revealThumbnail(thumbnail, 0);
        this.updateThumbnailFocusFrame();
      }
    }

    updateThumbnailFocusFrame() {
      const thumbnail = document.activeElement;
      if (!thumbnail?.matches(`${THUMBNAIL}:focus-visible`) || !this.contains(thumbnail)) return;
      window.AGFocusFrame?.updateCorners(thumbnail, thumbnail.closest(THUMBNAILS));
    }

    handleSubmit(event) {
      const button = this.querySelector(SUBMIT);
      const input = this.querySelector(VARIANT_INPUT);
      if (this.hasAttribute('aria-busy') || !button || button.disabled || !input?.value || input.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    initializeGallery() {
      const slider = this.querySelector(GALLERY);
      const images = Array.from(this.querySelectorAll(IMAGE));
      if (!slider || images.length < 2 || typeof window.Swiper !== 'function') return;
      const imageId = this.activeImageId || this.state.imageId;
      const initialSlide = Math.max(0, images.findIndex(image => image.dataset.agImage === String(imageId)));
      images.forEach(image => {
        image.hidden = false;
        image.querySelector('img').draggable = false;
      });
      this.motionPreference = window.matchMedia(REDUCED_MOTION);
      const thumbnails = this.querySelector(THUMBNAILS);
      if (thumbnails) {
        this.thumbnailGallery = new window.Swiper(thumbnails, {
          initialSlide,
          direction: 'horizontal',
          breakpoints: {
            1000: { direction: 'vertical' },
          },
          slidesPerView: 4,
          spaceBetween: 4,
          freeMode: { enabled: true, momentum: false, momentumBounce: false },
          resistanceRatio: 0,
          speed: this.motionPreference.matches ? 0 : GALLERY_SPEED,
          threshold: DRAG_THRESHOLD,
          watchOverflow: true,
          a11y: false,
        });
        this.thumbnailGallery.on('setTranslate resize', this.onThumbnailFocusFrame);
        this.onThumbnailWheel = this.handleThumbnailWheel.bind(this);
        thumbnails.addEventListener('wheel', this.onThumbnailWheel, { passive: false });
        this.thumbnailGallery.on('touchStart beforeTransitionStart beforeResize', () => this.cancelThumbnailWheel());
      }
      this.gallery = new window.Swiper(slider, {
        init: false,
        initialSlide,
        direction: 'horizontal',
        effect: 'fade',
        fadeEffect: { crossFade: true },
        speed: this.motionPreference.matches ? 0 : GALLERY_SPEED,
        grabCursor: true,
        threshold: DRAG_THRESHOLD,
        a11y: false,
        on: {
          slideChange: swiper => this.syncImage(swiper.slides[swiper.activeIndex]?.dataset.agImage, true),
        },
      });
      this.gallery.init();
      this.syncImage(images[initialSlide].dataset.agImage);
      this.onMotionChange = () => {
        this.cancelThumbnailWheel();
        this.gallery.params.speed = this.motionPreference.matches ? 0 : GALLERY_SPEED;
        if (this.thumbnailGallery) this.thumbnailGallery.params.speed = this.gallery.params.speed;
        if (this.motionPreference.matches) {
          this.gallery.slideTo(this.gallery.activeIndex, 0);
          this.thumbnailGallery?.slideTo(this.thumbnailGallery.activeIndex, 0);
        }
      };
      this.motionPreference.addEventListener('change', this.onMotionChange);
    }

    selectImage(imageId, reveal = false) {
      const images = Array.from(this.querySelectorAll(IMAGE));
      const index = images.findIndex(image => image.dataset.agImage === String(imageId));
      if (index < 0) return;
      if (this.gallery) this.gallery.slideTo(index);
      this.syncImage(imageId, reveal);
    }

    syncImage(imageId, reveal = false) {
      if (!imageId || !Array.from(this.querySelectorAll(IMAGE)).some(image => image.dataset.agImage === String(imageId))) return;
      this.activeImageId = String(imageId);
      this.querySelectorAll(GALLERY_CONTROL).forEach(control => {
        control.hidden = this.querySelectorAll(IMAGE).length < 2;
      });
      this.querySelectorAll(IMAGE).forEach(image => {
        const active = image.dataset.agImage === String(imageId);
        image.hidden = !this.gallery && !active;
        image.setAttribute('aria-hidden', String(!active));
      });
      this.querySelectorAll(THUMBNAIL).forEach(thumbnail => {
        const active = thumbnail.dataset.agThumbnail === String(imageId);
        thumbnail.setAttribute('aria-pressed', String(active));
        if (active && reveal) this.revealThumbnail(thumbnail);
      });
    }

    revealThumbnail(thumbnail, speed) {
      this.cancelThumbnailWheel();
      const rail = thumbnail.closest(THUMBNAILS) || thumbnail.parentElement;
      const box = thumbnail.getBoundingClientRect();
      const railBox = rail.getBoundingClientRect();
      const left = box.left < railBox.left ? box.left - railBox.left : Math.max(0, box.right - railBox.right);
      const top = box.top < railBox.top ? box.top - railBox.top : Math.max(0, box.bottom - railBox.bottom);
      if (this.thumbnailGallery) {
        if (Math.abs(left) > 1 || Math.abs(top) > 1) {
          this.thumbnailGallery.slideTo(this.thumbnailGallery.slides.indexOf(thumbnail), speed);
        }
      } else {
        rail.scrollBy({ left, top, behavior: speed === 0 || window.matchMedia(REDUCED_MOTION).matches ? 'instant' : 'smooth' });
      }
    }

    handleThumbnailWheel(event) {
      const swiper = this.thumbnailGallery;
      if (!swiper?.enabled || swiper.isLocked || event.ctrlKey || event.defaultPrevented || !event.cancelable) return;
      const horizontal = swiper.isHorizontal();
      const shiftToHorizontal = event.shiftKey && !event.deltaX;
      const x = shiftToHorizontal ? event.deltaY : event.deltaX;
      const y = shiftToHorizontal ? 0 : event.deltaY;
      if (horizontal ? Math.abs(x) <= Math.abs(y) : Math.abs(y) <= Math.abs(x)) return;
      const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? swiper.size : 1;
      const delta = -(horizontal ? x * (swiper.rtlTranslate ? -1 : 1) : y) * unit;
      const current = swiper.getTranslate();
      const start = swiper.minTranslate();
      const end = swiper.maxTranslate();
      // Leave outward wheel events to the page once the visible strip reaches an edge.
      if ((delta > 0 && current >= start - 0.5) || (delta < 0 && current <= end + 0.5)) {
        this.cancelThumbnailWheel();
        return;
      }
      event.preventDefault();
      if (swiper.animating) {
        swiper.setTranslate(current);
        swiper.transitionEnd(false);
      }
      const target = this.thumbnailWheelTarget;
      const base = target != null && Math.sign(target - current) === Math.sign(delta) ? target : current;
      this.thumbnailWheelTarget = Math.max(end, Math.min(start,
        base + delta * THUMBNAIL_WHEEL_SENSITIVITY));
      if (this.motionPreference.matches) {
        this.setThumbnailPosition(this.thumbnailWheelTarget);
        this.cancelThumbnailWheel();
        return;
      }
      if (this.thumbnailWheelFrame != null) return;
      this.thumbnailWheelTime = performance.now();
      const animate = (time) => {
        const position = swiper.getTranslate();
        const remaining = this.thumbnailWheelTarget - position;
        const elapsed = time - this.thumbnailWheelTime;
        this.thumbnailWheelTime = time;
        const next = Math.abs(remaining) <= 0.5 ? this.thumbnailWheelTarget
          : position + remaining * (1 - Math.exp(-elapsed / THUMBNAIL_WHEEL_EASING));
        this.setThumbnailPosition(next);
        if (next === this.thumbnailWheelTarget) this.cancelThumbnailWheel();
        else this.thumbnailWheelFrame = requestAnimationFrame(animate);
      };
      this.thumbnailWheelFrame = requestAnimationFrame(animate);
    }

    setThumbnailPosition(position) {
      const swiper = this.thumbnailGallery;
      swiper.setTransition(0);
      swiper.setTranslate(position);
      swiper.updateProgress();
      swiper.updateActiveIndex();
      swiper.updateSlidesClasses();
    }

    cancelThumbnailWheel() {
      if (this.thumbnailWheelFrame != null) cancelAnimationFrame(this.thumbnailWheelFrame);
      this.thumbnailWheelFrame = null;
      this.thumbnailWheelTarget = null;
    }

    handleChange(event) {
      if (event.target.matches(QUANTITY)) {
        this.syncQuantityControls();
        return;
      }
      if (!event.target.matches(OPTION)) return;
      const options = Array.from(this.querySelectorAll(OPTION), select => select.value);
      const variant = this.state.variants.find(candidate => candidate.options.every((value, index) => value === options[index]));
      this.controller?.abort();
      this.clearErrorMessage();
      if (!variant) {
        this.state.variantId = null;
        this.setUnavailable();
        this.updateUrl(null);
        return;
      }
      this.loadVariant(variant.id);
    }

    handlePopState() {
      if (this.dataset.updateUrl === 'false') return;
      const id = new URL(window.location.href).searchParams.get('variant');
      const selected = this.state.variants.find(variant => String(variant.id) === id);
      const cheapest = this.state.variants.reduce((lowest, variant) => !lowest || variant.price < lowest.price ? variant : lowest, null);
      if (selected || cheapest) this.loadVariant((selected || cheapest).id, false);
    }

    setUnavailable() {
      this.state.variantId = null;
      this.quantityController?.abort();
      this.removeAttribute('aria-busy');
      const input = this.querySelector(VARIANT_INPUT);
      input.value = '';
      input.disabled = true;
      const button = this.querySelector(SUBMIT);
      if (button) {
        button.disabled = true;
        button.querySelector('span').textContent = this.dataset.unavailable;
        const icon = button.querySelector('.ag-main-product__cart-icon');
        if (icon) icon.hidden = true;
      }
      const availability = this.querySelector('[data-ag-availability]');
      if (availability) {
        availability.classList.add('is-unavailable');
        availability.lastElementChild.textContent = this.dataset.unavailable;
        availability.firstElementChild.hidden = false;
      }
      this.querySelectorAll('.ag-main-product__prices').forEach(element => { element.hidden = false; });
      this.querySelectorAll('.ag-main-product__details').forEach(element => {
        element.hidden = true;
        const accordion = element.closest(ACCORDION);
        if (accordion) {
          const text = accordion.querySelector('.ag-main-product__accordion-text');
          const link = accordion.querySelector('.ag-main-product__document-link');
          accordion.hidden = !text?.textContent.trim() && !text?.querySelector('img, video, iframe') && !link;
        }
      });
    }

    updateUrl(id) {
      if (this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      if (id) url.searchParams.set('variant', id);
      else url.searchParams.delete('variant');
      window.history.replaceState({}, '', url);
    }

    sectionUrl(id) {
      const productUrl = this.dataset.originalSection ? this.dataset.productUrl : null;
      const url = new URL(productUrl || window.location.href, window.location.origin);
      url.searchParams.delete('option_values');
      url.searchParams.set('variant', id);
      url.searchParams.set('section_id', this.dataset.originalSection || this.dataset.section);
      return url;
    }

    async loadVariant(id, updateUrl = true) {
      this.clearErrorMessage();
      this.controller?.abort();
      this.quantityController?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setAttribute('aria-busy', 'true');
      const button = this.querySelector(SUBMIT);
      if (button) button.disabled = true;
      this.querySelector(VARIANT_INPUT).disabled = true;
      try {
        const response = await fetch(this.sectionUrl(id), { signal: controller.signal });
        if (!response.ok) throw new Error(`Product section: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (controller.signal.aborted || !this.isConnected) return;
        const incoming = html.querySelector(ELEMENT_NAME);
        if (!incoming?.querySelector(STATE) || !incoming.querySelector(SUMMARY)) throw new Error('Missing product section');
        const nextState = JSON.parse(incoming.querySelector(STATE).textContent);
        if (String(nextState.variantId) !== String(id)) throw new Error('Unexpected product variant');
        this.preProcessHtmlCallbacks.forEach(callback => callback(incoming));
        const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;
        const quantity = this.querySelector(QUANTITY)?.value;
        const openStates = new Map(Array.from(this.querySelectorAll(ACCORDION), item => [item.dataset.agAccordion, item.open]));
        incoming.querySelectorAll(ACCORDION).forEach(item => {
          if (openStates.has(item.dataset.agAccordion)) item.open = openStates.get(item.dataset.agAccordion);
        });
        this.querySelector(SUMMARY).replaceChildren(...incoming.querySelector(SUMMARY).childNodes);
        this.querySelector(CONTENT).replaceChildren(...incoming.querySelector(CONTENT).childNodes);
        this.state = nextState;
        this.querySelector(STATE).textContent = JSON.stringify(nextState);
        this.restoreQuantity(quantity);
        this.selectImage(nextState.imageId, true);
        this.clearErrorMessage();
        if (updateUrl) this.updateUrl(id);
        if (focusedId) this.querySelector(`#${CSS.escape(focusedId)}`)?.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('ag-main-product:updated', { bubbles: true }));
      } catch (error) {
        if (error.name === 'AbortError' || controller.signal.aborted) return;
        this.setUnavailable();
        this.showErrorMessage(this.dataset.error);
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }

    restoreQuantity(value) {
      const input = this.querySelector(QUANTITY);
      if (!input) return;
      const min = Number(input.min) || 1;
      const max = input.hasAttribute('max') ? Number(input.max) : Infinity;
      const step = Number(input.step) || 1;
      const rounded = min + Math.floor(Math.max(0, (Number(value) || min) - min) / step) * step;
      const validMax = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : max;
      input.value = String(Math.max(min, Math.min(rounded, validMax)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncQuantityControls() {
      const input = this.querySelector(QUANTITY);
      if (!input) return;
      const quantityControl = input.closest('quantity-input');
      if (typeof quantityControl?.validateQtyRules === 'function') {
        quantityControl.validateQtyRules();
        return;
      }
      const value = Number(input.value);
      const min = Number(input.min) || 1;
      const max = input.hasAttribute('max') ? Number(input.max) : Infinity;
      const step = Number(input.step) || 1;
      const minus = this.querySelector(MINUS);
      const plus = this.querySelector(PLUS);
      if (minus) minus.disabled = input.disabled || value - step < min;
      if (plus) plus.disabled = input.disabled || value + step > max;
    }

    async refreshQuantityRules() {
      const id = this.state.variantId;
      if (!id || this.hasAttribute('aria-busy')) return;
      this.quantityController?.abort();
      const controller = new AbortController();
      this.quantityController = controller;
      try {
        const response = await fetch(this.sectionUrl(id), { signal: controller.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (controller.signal.aborted || this.hasAttribute('aria-busy') || this.state.variantId !== id || !this.isConnected) return;
        const incoming = html.querySelector(ELEMENT_NAME);
        const nextVariantInput = incoming?.querySelector(VARIANT_INPUT);
        if (!nextVariantInput || String(nextVariantInput.value) !== String(id)) return;
        const source = incoming.querySelector(QUANTITY);
        const input = this.querySelector(QUANTITY);
        if (source && input) {
          for (const name of ['min', 'max', 'step', 'data-min', 'data-cart-quantity']) {
            if (source.hasAttribute(name)) input.setAttribute(name, source.getAttribute(name));
            else input.removeAttribute(name);
          }
          this.restoreQuantity(input.value);
        }
        const nextButton = incoming.querySelector(SUBMIT);
        const button = this.querySelector(SUBMIT);
        if (nextButton && button) {
          button.disabled = nextButton.disabled;
          button.querySelector('span').textContent = nextButton.querySelector('span').textContent;
          const icon = button.querySelector('.ag-main-product__cart-icon');
          const nextIcon = nextButton.querySelector('.ag-main-product__cart-icon');
          if (icon && nextIcon) icon.hidden = nextIcon.hidden;
        }
        this.querySelector(VARIANT_INPUT).disabled = nextVariantInput.disabled;
        const availability = this.querySelector('[data-ag-availability]');
        const nextAvailability = incoming.querySelector('[data-ag-availability]');
        if (availability && nextAvailability) {
          availability.classList.toggle('is-unavailable', nextAvailability.classList.contains('is-unavailable'));
          availability.replaceChildren(...nextAvailability.childNodes);
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }
  }

  if (!customElements.get(ELEMENT_NAME)) customElements.define(ELEMENT_NAME, AgMainProduct);
})();
